import DealCheckout from '@/pages/seller/DealCheckout';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { RoleProtectedRoute } from "@/components/RoleProtectedRoute";
import { CookieBanner } from "@/components/cookies/CookieBanner";
import { useEffect } from "react";
import Index from "./pages/Index";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import I18nManager from "./pages/I18nManager";
import Submissions from "./pages/Submissions";
import Deals from "./pages/Deals";
import Messages from "./pages/Messages";
import Documents from "./pages/Documents";
import Favorites from "./pages/Favorites";
import Partners from "./pages/Partners";
import Consultants from "./pages/Consultants";
import AdminPortal from "./pages/AdminPortal";
import CIMReview from "./pages/CIMReview";
import AdminSetRole from "./pages/AdminSetRole";
import SetRole from "./pages/admin/SetRole";
import SubmitDeal from "./pages/SubmitDeal";
import RegisterBroker from "./pages/auth/RegisterBroker";
import RegisterBuyer from "./pages/auth/RegisterBuyer";
import RegisterConsultant from "./pages/auth/RegisterConsultant";
import RegisterLender from "./pages/auth/RegisterLender";
import RegisterPartner from "./pages/auth/RegisterPartner";
import RegisterSellers from "./pages/auth/RegisterSellers";
import DealFormPublic from "./pages/DealFormPublic";
import BackgroundCheck from "./pages/auth/BackgroundCheck";
import VerificationComplete from "./pages/auth/verification-complete";
import { RequireAccountActive } from "@/components/RequireAccountActive";
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import DealRoom from "./pages/DealRoom";

// Buyer dashboard - new implementation
import BuyerDashboard from "./pages/buyer/BuyerDashboard";
import BuyerConsultants from "./pages/buyer/BuyerConsultants";
import BuyerUpgrade from "./pages/buyer/BuyerUpgrade";
import SellerDashboard from "./pages/seller/SellerDashboard";
import SellerConsultants from "./pages/seller/SellerConsultants";
import BrokerDashboard from "./pages/dashboard/broker/BrokerDashboard";
import BrokerCalendar from "./pages/dashboard/broker/BrokerCalendar";
import BrokerListings from "./pages/dashboard/broker/BrokerListings";
import BrokerDeals from "./pages/dashboard/broker/BrokerDeals";
import BrokerPartners from "./pages/dashboard/broker/BrokerPartners";
import BrokerConsultants from "./pages/dashboard/broker/BrokerConsultants";
import BrokerDocuments from "./pages/dashboard/broker/BrokerDocuments";
import BrokerMessages from "./pages/dashboard/broker/BrokerMessages";
import BrokerVideo from "./pages/dashboard/broker/BrokerVideo";
import ConsultantDashboard from "./pages/dashboard/consultant/ConsultantDashboard";
import ConsultantUpgrade from "./pages/consultant/ConsultantUpgrade";
import ConsultantVideo from "./pages/dashboard/consultant/ConsultantVideo";
import ConsultantClients from "./pages/dashboard/consultant/ConsultantClients";
import ConsultantPartnerships from "./pages/dashboard/consultant/ConsultantPartnerships";
import ConsultantMandates from "./pages/dashboard/consultant/ConsultantMandates";
import NewMandateForm from "./components/consultant/NewMandateForm";
import ConsultantCalendar from "./pages/dashboard/consultant/ConsultantCalendar";
import ConsultantDocuments from "./pages/dashboard/consultant/ConsultantDocuments";
import ConsultantMessages from "./pages/dashboard/consultant/ConsultantMessages";
import AddClient from "./pages/consultant/AddClient";
import NewMandate from "./pages/consultant/NewMandate";
import ConsultantAnalytics from "./pages/consultant/ConsultantAnalytics";
import ScheduleMeeting from "./pages/consultant/ScheduleMeeting";
// Layout components for remaining dashboards
import BrokerLayout from "./components/layout/BrokerLayout";
import ConsultantLayout from "./components/layout/ConsultantLayout";
import LenderLayout from "./components/layout/LenderLayout";
// BuyerLayout and SellerLayout removed - consolidated to /dashboard
// DealMonitor is now replaced by Listings page - redirect handled in routes
import DealDetail from "./pages/DealDetail";
import Seed from "./pages/Seed";
import LawyerCases from "./pages/lawyer/LawyerCases";
import LoanOffers from "./pages/lender/mockdeals/LoanOffers";
import LenderIndex from "./pages/lender/LenderIndex";
import LenderCalendar from "./pages/lender/Calendar";
import LenderDocuments from "./pages/lender/Documents";
import LenderMessages from "./pages/lender/Messages";
import LenderPipeline from "./pages/lender/Pipeline";
import LenderSubmissions from "./pages/lender/Submissions";
import LenderConsultants from "./pages/lender/Consultants";
import SubmitLenderSubmission from "./pages/lender/SubmitLenderSubmission";
import SeedSubmissions from "./pages/lender/SeedSubmissions";
import VideoConference from "./pages/lender/VideoConference";
// Legacy partner pages (kept for reference, routes simplified)
import InternalPartnerLayout from "./pages/partners/InternalPartnerLayout";
import { RequirePartnerAddon } from "./components/partners/RequirePartnerAddon";
import { PartnerOverview, PartnerClients, PartnerMandates, PartnerNewMandate, PartnerCalendar, PartnerDocuments, PartnerMessages, PartnerVideoConference, PartnerAnalytics } from "./pages/partners/internal";
import UserProfile from "./pages/profile/UserProfile";
import PublicProfileView from "./pages/profile/PublicProfileView";
import Settings from "./pages/Settings";
import BuyerProfile from "./pages/buyer/BuyerProfile";
import SellerProfile from "./pages/seller/SellerProfile";
import ConsultantProfile from "./pages/consultant/ConsultantProfile";
import BrokerProfile from "./pages/dashboard/broker/BrokerProfile";
import LenderProfile from "./pages/lender/LenderProfile";
import PartnerProfile from "./pages/partners/PartnerProfile";
import PartnerBilling from "./pages/partners/PartnerBilling";
import PartnerPlans from "./pages/partners/PartnerPlans";
import NotFound from "./pages/NotFound";
import EarlyAccess from "./pages/EarlyAccess";
import EarlyAccessPartners from "./pages/EarlyAccessPartners";
// Seller dashboard components removed - consolidated to /dashboard
import CPAQuestionnaire from "./pages/tools/CPAQuestionnaire";
import LenderSubmission from "./pages/lender/LenderSubmission";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import NDADocument from "./pages/tools/NDADocument";
import LOIGenerator from "./pages/tools/LOIGenerator";
import LenderOfferForm from "./components/forms/LenderOfferForm";
import LenderDealPreview from "./pages/lender/LenderDealPreview";
import RemoteFormPage from "./pages/tools/RemoteFormPage";
import About from "./pages/About";
import Blog from "./pages/Blog";
import PostPage from "./blog/PostPage";
import GeneralLanding from "./pages/audiences/GeneralLanding";
import BrokerSellerPage from "./pages/audiences/BrokerSellerPage";
import LendersPage from "./pages/audiences/LendersPage";
import PartnersPage from "./pages/audiences/PartnersPage";
import ConsultantsPage from "./pages/audiences/ConsultantsPage";
import SellersLanding from "./pages/audiences/SellersLanding";
import BuyersLanding from "./pages/audiences/BuyersLanding";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLayout from "./components/admin/AdminLayout";
import AdminOverview from "./pages/admin/Overview";
import AdminDealFlow from "./pages/admin/DealFlow";
import AdminUsersManagement from "./pages/admin/UsersManagement";
import AdminLendersOffers from "./pages/admin/LendersOffers";
import AdminCIMEvaluation from "./pages/admin/CIMEvaluation";
import CIMReviewStaff from "./pages/admin/CIMReviewStaff";
import CIMPreview from "./pages/CIMPreview";
import AdminFinancePayments from "./pages/admin/FinancePayments";
import AdminStaffProductivity from "./pages/admin/StaffProductivity";
import AdminConsultantsPartners from "./pages/admin/ConsultantsPartners";
import AdminPerformanceStats from "./pages/admin/PerformanceStats";
import AdminGenerateCIM from "./pages/admin/GenerateCIM";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCallCenter from "./pages/admin/CallCenter";
import AdminCommunications from "./pages/admin/Communications";
import AdminCalendar from "./pages/admin/AdminCalendar";
import MarketStudyGenerator from "./pages/admin/MarketStudyGenerator";
import StrategicPlanGenerator from "./pages/admin/StrategicPlanGenerator";
import CompetitionReportGenerator from "./pages/admin/CompetitionReportGenerator";
import StaffLayout from "./layouts/StaffLayout";
import StaffDashboard from "./pages/staff/Dashboard";
import StaffUsers from "./pages/staff/Users";
import StaffCompanies from "./pages/staff/Companies";
import StaffDeals from "./pages/staff/Deals";
import StaffDealDetail from "./pages/staff/DealDetail";
import StaffDealsPipeline from "./pages/staff/DealsPipeline";
import StaffTodo from "./pages/staff/Todo";
import StaffTriage from "./pages/staff/Triage";
import StaffPipeline from "./pages/staff/Pipeline";
import StaffVerifications from "./pages/staff/Verifications";
import StaffSubmissions from "./pages/staff/Submissions";
import StaffMessages from "./pages/staff/Messages";
import StaffCalls from "./pages/staff/Calls";
import StaffCommunications from "./pages/staff/Communications";
import StaffDocuments from "./pages/staff/Documents";
import StaffPartners from "./pages/staff/Partners";
import StaffExperts from "./pages/staff/Experts";
import StaffOperations from "./pages/staff/Operations";
import StaffCalendar from "./pages/staff/Calendar";
import StaffFinance from "./pages/staff/Finance";
import StaffCorporateDocuments from "./pages/staff/CorporateDocuments";
import StaffMyAvailability from "./pages/staff/MyAvailability";
import AdminStaffAvailability from "./pages/admin/StaffAvailability";
import AdminCorporateDocuments from "./pages/admin/CorporateDocuments";
import ELearningDashboard from "./pages/resources/ELearningDashboard";
import ModulePage from "./pages/resources/ModulePage";
import KpiDashboard from "./pages/KpiDashboard";
import Listings from "./pages/Listings";
import ListingDetail from "./pages/ListingDetail";
import RealListingDetail from "./pages/RealListingDetail";
import PaymentSuccess from "./pages/PaymentSuccess";
import DevSendWelcomeTest from "./pages/DevSendWelcomeTest";
import PaymentCanceled from "./pages/PaymentCanceled";
import ConsultantPayment from "./pages/auth/ConsultantPayment";
import BuyerPayment from "./pages/auth/BuyerPayment";
import SellerPlan from "./pages/auth/SellerPlan";
import MeetingRoomPage from "./pages/meetings/MeetingRoomPage";
import MeetingHistoryPage from "./pages/meetings/MeetingHistoryPage";
import TwilioSetupGuide from "./pages/docs/TwilioSetupGuide";
import ReferencePage from "./pages/docs/ReferencePage";
import BookingPage from "./pages/BookingPage";
import BookingSettings from "./pages/BookingSettings";
import AcquizGroupFullTerms from "./pages/legal/AcquizGroupFullTerms";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiesPolicy from "./pages/legal/CookiesPolicy";
import RegisterDealSourcer from "./pages/auth/RegisterDealSourcer";
import DealSourcerLayout from "./components/layout/DealSourcerLayout";
import DealSourcerDashboard from "./pages/dashboard/deal-sourcer/DealSourcerDashboard";
import DealSourcerSubmissions from "./pages/dashboard/deal-sourcer/DealSourcerSubmissions";
import DealSourcerCommissions from "./pages/dashboard/deal-sourcer/DealSourcerCommissions";
import DealSourcerMessages from "./pages/dashboard/deal-sourcer/DealSourcerMessages";
import DealSourcerProfile from "./pages/dashboard/deal-sourcer/DealSourcerProfile";
import StaffMyWork from "./pages/staff/MyWork";

// Scroll to top on route change
function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return null;
}

// Le Dispatcher centralisé vit maintenant dans src/pages/Dashboard.tsx
// Source unique de mapping rôle → route : src/lib/roles.ts (ROLE_DASHBOARD_MAP)

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    const currentPath = window.location.pathname + window.location.search;
    sessionStorage.setItem('intended_destination', currentPath);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  useDocumentMeta();
  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CookieBanner />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Index />} />
          {/* Dispatcher centralisé : /dashboard → bon dashboard selon le rôle */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/general-landing" element={<GeneralLanding />} />
          <Route path="/landing/sellers" element={<SellersLanding />} />
          <Route path="/landing/buyers" element={<BuyersLanding />} />
          <Route path="/landing/brokers" element={<BrokerSellerPage />} />
          <Route path="/landing/consultants" element={<ConsultantsPage />} />
          <Route path="/landing/partners" element={<PartnersPage />} />
          <Route path="/partners/business-law" element={<Navigate to="/landing/partners" replace />} />
          <Route path="/partners/immigration-law" element={<Navigate to="/landing/partners" replace />} />
          <Route path="/partners/notary" element={<Navigate to="/landing/partners" replace />} />
          <Route path="/partners/tax-advisor" element={<Navigate to="/landing/partners" replace />} />
          <Route path="/landing/lenders" element={<LendersPage />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<AcquizGroupFullTerms />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookies" element={<CookiesPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<PostPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register/brokers" element={<RegisterBroker />} />
          <Route path="/register/buyers" element={<RegisterBuyer />} />
          <Route path="/register/consultant" element={<RegisterConsultant />} />
          <Route path="/register/deal-sourcer" element={<RegisterDealSourcer />} />
          <Route path="/register/lender" element={<RegisterLender />} />
          <Route path="/register/partner" element={<RegisterPartner />} />
          <Route path="/register/sellers" element={<RegisterSellers />} />
          //formulaire de deal public partagé par un broker à un
        vendeur représenté (voir dealFormDrafts). Volontairement hors de
        ProtectedRoute : le vendeur n'a pas de compte à cette étape,
        l'accès est protégé par le token lui-même (voir firestore.rules
        dealFormDrafts). */
    <Route path="/deal-form/:token" element={<DealFormPublic />} />
          <Route path="/background-check" element={<BackgroundCheck />} />
          <Route path="/verification-complete" element={<VerificationComplete />} />
          <Route path="/sellerplan" element={<SellerPlan />} />
          <Route path="/listings" element={<Listings />} />
          <Route path="/listings/:slug" element={<RealListingDetail />} />
          <Route path="/book/:hostId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
          <Route path="/cim-preview/:dealId" element={<CIMPreview />} />
          <Route path="/docs/reference" element={<ReferencePage />} />
          <Route path="/deal-room/:dealId" element={<DealRoom />} />
          {/* Route /dashboard gérée plus haut par le Dispatcher centralisé */}
          <Route
            path="/submissions"
            element={
              <ProtectedRoute>
                <Submissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals"
            element={
              <ProtectedRoute>
                <Deals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <Favorites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/booking-settings"
            element={
              <ProtectedRoute>
                <BookingSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/partners" element={<Navigate to="/internal-partners" replace />} />
          <Route path="/partners/dashboard" element={<Navigate to="/internal-partners" replace />} />
          <Route
            path="/consultants"
            element={
              <ProtectedRoute>
                <Consultants />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-portal"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <AdminPortal />
              </RoleProtectedRoute>
            }
          />
          {/* Admin Dashboard - Redirect to new layout */}
          <Route path="/admin-dashboard" element={<Navigate to="/admin/overview" replace />} />

          {/* New Super Admin Dashboard - Parent/Child Layout Structure */}
          <Route
            path="/admin"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <AdminLayout />
              </RoleProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/overview" replace />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="deal-flow" element={<AdminDealFlow />} />
            <Route path="users" element={<AdminUsersManagement />} />
            <Route path="users/sellers" element={<AdminUsersManagement />} />
            <Route path="users/brokers" element={<AdminUsersManagement />} />
            <Route path="users/buyers" element={<AdminUsersManagement />} />
            <Route path="users/byod" element={<AdminUsersManagement />} />
            <Route path="create-user" element={<AdminSetRole />} />
            <Route path="lenders" element={<AdminLendersOffers />} />
            <Route path="cim-eval" element={<AdminCIMEvaluation />} />
            <Route path="cim-review/:dealId" element={<CIMReviewStaff />} />
            <Route path="ai/market-study" element={<MarketStudyGenerator />} />
            <Route path="ai/strategic-plan" element={<StrategicPlanGenerator />} />
            <Route path="ai/competition-report" element={<CompetitionReportGenerator />} />
            <Route path="finance" element={<AdminFinancePayments />} />
            <Route path="staff" element={<AdminStaffProductivity />} />
            <Route path="staff/todo-all" element={<AdminStaffProductivity />} />
            <Route path="staff/deals" element={<AdminStaffProductivity />} />
            <Route path="deals/:dealId" element={<StaffDealDetail />} />
            <Route path="staff/pipeline" element={<AdminStaffProductivity />} />
            <Route path="staff/load" element={<AdminStaffProductivity />} />
            <Route path="staff/roles" element={<AdminStaffProductivity />} />
            <Route path="partners" element={<AdminConsultantsPartners />} />
            <Route path="partners/assignments" element={<AdminConsultantsPartners />} />
            <Route path="partners/internal" element={<AdminConsultantsPartners />} />
            <Route path="partners/external" element={<AdminConsultantsPartners />} />
            {/* Aliases: "Our Experts" (internal_partner) and external consultants */}
            <Route path="experts" element={<AdminConsultantsPartners />} />
            <Route path="experts/assignments" element={<AdminConsultantsPartners />} />
            <Route path="experts/list" element={<AdminConsultantsPartners />} />
            <Route path="consultants" element={<AdminConsultantsPartners />} />
            <Route path="consultants/performance" element={<AdminConsultantsPartners />} />
            <Route path="consultants/list" element={<AdminConsultantsPartners />} />
            {/* Generate CIM / Starter Pack */}
            <Route path="generate/cim" element={<Navigate to="/staff/my-work" replace />} />
            {/* Admin alias for staff triage (Quick Action target) */}
            <Route path="staff/triage" element={<StaffTriage />} />
            {/* Performance & search stats */}
            <Route path="performance" element={<AdminPerformanceStats />} />
            <Route path="analytics/search-stats" element={<AdminPerformanceStats />} />
            <Route path="forms/lender-offer" element={<LenderOfferForm />} />
            <Route path="content" element={<AdminOverview />} />
            <Route path="search" element={<AdminOverview />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="call-center" element={<AdminCallCenter />} />
            <Route path="corporate-docs" element={<AdminCorporateDocuments />} />
            <Route path="staff-availability" element={<AdminStaffAvailability />} />
            <Route path="communications" element={<AdminCommunications />} />
            <Route path="calendar" element={<AdminCalendar />} />
          </Route>
          <Route
            path="/cim-review"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <CIMReview />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/admin-set-role"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <AdminSetRole />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/admin/set-role"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <SetRole />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/kpi-dashboard"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <KpiDashboard />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/submit-deal"
            element={
              <ProtectedRoute>
                <SubmitDeal />
              </ProtectedRoute>
            }
          />
          {/* Buyer Dashboard */}
          <Route
            path="/buyer/dashboard"
            element={
              <ProtectedRoute>
                <BuyerDashboard />
              </ProtectedRoute>
            }
          />
          {/* Buyer Upgrade */}
          <Route
            path="/buyer/upgrade"
            element={
              <ProtectedRoute>
                <BuyerUpgrade />
              </ProtectedRoute>
            }
          />
          {/* Buyer Consultants Page */}
          <Route
            path="/buyer/consultants"
            element={
              <ProtectedRoute>
                <BuyerConsultants />
              </ProtectedRoute>
            }
          />
          {/* Redirect old buyer dashboard routes to new buyer dashboard */}
          <Route path="/dashboard/buyer/*" element={<Navigate to="/buyer/dashboard" replace />} />

          {/* Seller Dashboard */}
          <Route
            path="/seller/dashboard"
            element={
              <ProtectedRoute>
                <SellerDashboard />
              </ProtectedRoute>
            }
          />
          {/* Redirect old seller dashboard routes to new seller dashboard */}
          <Route path="/dashboard/seller/*" element={<Navigate to="/seller/dashboard" replace />} />

          {/* Broker Dashboard - Redirect all sub-routes to main dashboard */}
          <Route
            path="/dashboard/broker"
            element={
              <ProtectedRoute>
                <BrokerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/consultants"
            element={
              <ProtectedRoute>
                <BrokerConsultants />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/broker/listings"
            element={
              <ProtectedRoute>
                <BrokerListings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/deals"
            element={
              <ProtectedRoute>
                <BrokerDeals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/partners"
            element={
              <ProtectedRoute>
                <BrokerPartners />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/documents"
            element={
              <ProtectedRoute>
                <BrokerDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/messages"
            element={
              <ProtectedRoute>
                <BrokerMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/calendar"
            element={
              <ProtectedRoute>
                <BrokerCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/broker/video"
            element={
              <ProtectedRoute>
                <BrokerVideo />
              </ProtectedRoute>
            }
          />

          {/* Deal Sourcer Dashboard */}
          <Route path="/dashboard/deal-sourcer" element={<ProtectedRoute><DealSourcerLayout /></ProtectedRoute>}>
            <Route index element={<DealSourcerDashboard />} />
            <Route path="submissions" element={<DealSourcerSubmissions />} />
            <Route path="commissions" element={<DealSourcerCommissions />} />
            <Route path="messages" element={<DealSourcerMessages />} />
            <Route path="profile" element={<DealSourcerProfile />} />
          </Route>

          {/* Seller Consultants Page */}
          <Route
            path="/seller/consultants"
            element={
              <ProtectedRoute>
                <SellerConsultants />
              </ProtectedRoute>
            }
          />

          {/* Deal Checkout — Seller, Broker, BYOD */}
          <Route
            path="/seller/deal-checkout"
            element={
              <ProtectedRoute>
                <DealCheckout />
              </ProtectedRoute>
            }
          />

          {/* Consultant Dashboard - Parent/Child Layout Structure */}
          <Route
            path="/dashboard/consultant"
            element={
              <ProtectedRoute>
                <RequireAccountActive role="consultant">
                  <ConsultantLayout />
                </RequireAccountActive>
              </ProtectedRoute>
            }
          >
            <Route index element={<ConsultantDashboard />} />
            <Route path="clients" element={<ConsultantClients />} />
            <Route path="partnerships" element={<ConsultantPartnerships />} />
            <Route path="mandates" element={<ConsultantMandates />} />
            <Route path="new-mandate" element={<NewMandateForm />} />
            <Route path="calendar" element={<ConsultantCalendar />} />
            <Route path="documents" element={<ConsultantDocuments />} />
            <Route path="messages" element={<ConsultantMessages />} />
            <Route path="analytics" element={<ConsultantAnalytics />} />
            <Route path="video" element={<ConsultantVideo />} />
          </Route>
          {/* Redirect old /deal-monitor to /listings */}
          <Route path="/deal-monitor" element={<Navigate to="/listings" replace />} />
          <Route
            path="/deals/:id"
            element={
              <ProtectedRoute>
                <DealDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seed"
            element={
              <ProtectedRoute>
                <Seed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/i18n-manager"
            element={
              <ProtectedRoute>
                <I18nManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lawyer/cases"
            element={
              <ProtectedRoute>
                <LawyerCases />
              </ProtectedRoute>
            }
          />
          {/* Lender Dashboard - Parent/Child Layout Structure */}
          <Route path="/lender" element={<Navigate to="/dashboard/lender" replace />} />
          <Route
            path="/dashboard/lender"
            element={
              <ProtectedRoute>
                <RequireAccountActive role="lender">
                  <LenderLayout />
                </RequireAccountActive>
              </ProtectedRoute>
            }
          >
            <Route index element={<LenderIndex />} />
            <Route path="pipeline" element={<LenderPipeline />} />
            <Route path="loan-offers" element={<LoanOffers />} />
            <Route path="consultants" element={<LenderConsultants />} />
            <Route path="calendar" element={<LenderCalendar />} />
            <Route path="documents" element={<LenderDocuments />} />
            <Route path="messages" element={<LenderMessages />} />
            <Route path="video" element={<VideoConference />} />
          </Route>

          {/* Lender Utility Routes (outside layout) */}
          <Route
  path="/lender/submit-offer"
  element={
    <ProtectedRoute>
      <LenderOfferForm />
    </ProtectedRoute>
  }
/>
          <Route
            path="/lender/seed-submissions"
            element={
              <ProtectedRoute>
                <SeedSubmissions />
              </ProtectedRoute>
            }
          />
          {/* Internal Partners - Redirect to first partner type */}
          <Route
            path="/internal-partners"
            element={<Navigate to="/internal-partners/business-law" replace />}
          />

          {/* Internal Partner Full Dashboards */}
          <Route
            path="/internal-partners/:partnerType"
            element={
              <ProtectedRoute>
                <RequireAccountActive role="partner">
                  <RequirePartnerAddon>
                    <InternalPartnerLayout />
                  </RequirePartnerAddon>
                </RequireAccountActive>
              </ProtectedRoute>
            }
          >
            <Route index element={<PartnerOverview />} />
            <Route path="overview" element={<PartnerOverview />} />
            <Route path="clients" element={<PartnerClients />} />
            <Route path="mandates" element={<PartnerMandates />} />
            <Route path="new-mandate" element={<PartnerNewMandate />} />
            <Route path="calendar" element={<PartnerCalendar />} />
            <Route path="documents" element={<PartnerDocuments />} />
            <Route path="messages" element={<PartnerMessages />} />
            <Route path="video" element={<PartnerVideoConference />} />
            <Route path="analytics" element={<PartnerAnalytics />} />
          </Route>

          {/* User Profile */}
          <Route path="/profile" element={<ProtectedRoute><UserProfile /></ProtectedRoute>} />
          <Route path="/profile/public/:uid" element={<ProtectedRoute><PublicProfileView /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/notifications" element={<Navigate to="/settings#notifications" replace />} />
          <Route path="/buyer/profile" element={<ProtectedRoute><div className="container mx-auto p-6 max-w-5xl"><BuyerProfile /></div></ProtectedRoute>} />
          <Route path="/seller/profile" element={<ProtectedRoute><SellerProfile /></ProtectedRoute>} />
          <Route path="/consultant/profile" element={<ProtectedRoute><ConsultantProfile /></ProtectedRoute>} />
          <Route path="/dashboard/broker/profile" element={<ProtectedRoute><BrokerProfile /></ProtectedRoute>} />
          <Route path="/lender/profile" element={<ProtectedRoute><LenderProfile /></ProtectedRoute>} />
          <Route path="/lender/deal-preview/:dealId" element={<ProtectedRoute><LenderDealPreview /></ProtectedRoute>} />
          <Route path="/forms/lender-offer" element={<ProtectedRoute><LenderOfferForm /></ProtectedRoute>} />
          <Route path="/partners/profile" element={<ProtectedRoute><PartnerProfile /></ProtectedRoute>} />
          <Route path="/partner/plans" element={<PartnerPlans />} />
          <Route path="/partner/billing" element={<ProtectedRoute><PartnerBilling /></ProtectedRoute>} />
          {/* Redirect old seller dashboard routes to main dashboard */}
          <Route path="/seller" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard/seller/*" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/tools/cpa-questionnaire"
            element={
              <ProtectedRoute>
                <CPAQuestionnaire />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lender/submission"
            element={
              <ProtectedRoute>
                <LenderSubmission />
              </ProtectedRoute>
            }
          />
          <Route
            path="/finance/dashboard"
            element={
              <ProtectedRoute>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/loi-generator"
            element={
              <ProtectedRoute>
                <LOIGenerator />
              </ProtectedRoute>
            }
          />
          <Route path="/tools/nda-generator" element={<NDADocument />} />
          <Route path="/tools/form/:slug" element={<RemoteFormPage />} />

          {/* Staff Portal - Parent/Child Layout Structure */}
          <Route
            path="/staff"
            element={
              <ProtectedRoute>
                <StaffLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<StaffDashboard />} />
            <Route path="my-work" element={<StaffMyWork />} />
            <Route path="todo" element={<Navigate to="/staff/my-work?tab=todo" replace />} />
            <Route path="triage" element={<Navigate to="/staff/my-work?tab=triage" replace />} />
            <Route path="deals" element={<Navigate to="/staff/my-work?tab=active" replace />} />
            <Route path="submissions" element={<Navigate to="/staff/my-work?tab=triage" replace />} />
            <Route path="deals/:dealId" element={<StaffDealDetail />} />
            <Route path="ai/market-study" element={<MarketStudyGenerator />} />
            <Route path="ai/strategic-plan" element={<StrategicPlanGenerator />} />
            <Route path="pipeline" element={<StaffPipeline />} />
            <Route path="verifications" element={<StaffVerifications />} />
            <Route path="users" element={<StaffUsers />} />
            <Route path="companies" element={<StaffCompanies />} />
            <Route path="deals-pipeline" element={<StaffDealsPipeline />} />
            <Route path="messages" element={<StaffMessages />} />
            <Route path="calls" element={<StaffCalls />} />
            <Route path="communications" element={<StaffCommunications />} />
            <Route path="documents" element={<StaffDocuments />} />
            <Route path="partners" element={<StaffPartners />} />
            <Route path="experts" element={<StaffExperts />} />
            <Route path="operations" element={<StaffOperations />} />
            <Route path="calendar" element={<StaffCalendar />} />
            <Route path="finance" element={<StaffFinance />} />
            <Route path="corporate-docs" element={<StaffCorporateDocuments />} />
            <Route path="my-availability" element={<StaffMyAvailability />} />
          </Route>

          {/* E-Learning Resources */}
          <Route path="/resources/e-learning" element={<ELearningDashboard />} />
          <Route path="/e-learning/:categoryId/:moduleId" element={<ModulePage />} />

          {/* Meeting Pages */}
          <Route
            path="/meetings"
            element={
              <ProtectedRoute>
                <MeetingHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meetings/:meetingId"
            element={
              <ProtectedRoute>
                <MeetingRoomPage />
              </ProtectedRoute>
            }
          />

          {/* Consultant Pages */}
          <Route
            path="/consultant/add-client"
            element={
              <ProtectedRoute>
                <AddClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/new-mandate"
            element={
              <ProtectedRoute>
                <NewMandate />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/analytics"
            element={
              <ProtectedRoute>
                <ConsultantAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/schedule-meeting"
            element={
              <ProtectedRoute>
                <ScheduleMeeting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/consultant/upgrade"
            element={
              <ProtectedRoute>
                <ConsultantUpgrade />
              </ProtectedRoute>
            }
          />
          {/* Payment Pages */}
          <Route path="/consultant/payment" element={<ConsultantPayment />} />
          <Route path="/buyer/payment" element={<BuyerPayment />} />
          <Route path="/buyer-plans" element={<BuyerPayment />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-canceled" element={<PaymentCanceled />} />
          <Route path="/dev/send-welcome-test" element={<DevSendWelcomeTest />} />

          {/* Documentation Pages */}
          <Route
            path="/docs/twilio-setup"
            element={
              <RoleProtectedRoute allowedRoles={["admin", "staff"]}>
                <TwilioSetupGuide />
              </RoleProtectedRoute>
            }
          />

          <Route path="/early-access" element={<EarlyAccess />} />
          <Route path="/early-access/partners" element={<EarlyAccessPartners />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;